import { type ReactNode } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import FabButton, { type FabVariant } from "./FabButton";

type FabPopoverProperties = {
  /** Accessible label for the FAB — also shown as its tooltip. */
  label: string;
  /** Icon rendered inside the FAB. */
  icon: ReactNode;
  /** Visual accent variant. */
  variant: FabVariant;
  /** ID of the popover content panel — wired to the FAB's aria-controls. */
  panelId: string;
  /** Whether the popover is open. */
  open: boolean;
  /** Controlled open/close handler. */
  onOpenChange: (open: boolean) => void;
  /** Disables the FAB trigger (e.g. read-only mode). */
  disabled?: boolean;
  /** Optional class names for the popover content (e.g. width). */
  contentClassName?: string;
  /** Popover body. */
  children: ReactNode;
};

/**
 * Shared scaffolding for a FAB-triggered menu popover: anchors a {@link FabButton}
 * to a top/end-aligned {@link PopoverContent} and wires the controlled open state
 * plus the menu-popover a11y contract in one place. Used by AnalystNotesPopover
 * and ActionsPopover. The InternalAuditDrawer intentionally does not use this —
 * it renders a Sheet, not a popover.
 */
function FabPopover({
  label,
  icon,
  variant,
  panelId,
  open,
  onOpenChange,
  disabled,
  contentClassName,
  children,
}: FabPopoverProperties) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <FabButton
          label={label}
          icon={icon}
          variant={variant}
          expanded={open}
          controls={panelId}
          hasPopup="menu"
          disabled={disabled}
          onClick={() => onOpenChange(!open)}
        />
      </PopoverTrigger>
      <PopoverContent
        id={panelId}
        side="top"
        align="end"
        sideOffset={12}
        className={contentClassName}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export default FabPopover;
